/**
 * One content element, drawn on the canvas exactly as the site will draw it.
 *
 * Mirrors the `Block` switch in `SiteRenderer.tsx` (zoea-website-module),
 * using the mirrored token layer in `./tokens`. The markup and class names
 * match, so `sites-elements.css` (itself a mirror of the renderer's
 * stylesheet) paints both identically.
 *
 * The one deliberate difference is interaction: inside the Builder a link
 * must not navigate. Buttons render as the same element with the same
 * classes, but with navigation suppressed -- clicking one selects it. Without
 * that, editing a call-to-action would eject the user from the Builder.
 */
import React from 'react';

import type { ContentBlock, SiteChrome } from '../types';
import { InlineText } from '../components/InlineText';
import { CanvasIcon } from './CanvasIcon';
import {
  effectiveHover,
  resolveButton,
  resolveDestination,
  resolveSurface,
  resolveVisualSurface,
  legacyButtonPresentation,
} from './tokens';

type Theme = SiteChrome['theme'];

interface CanvasElementProps {
  block: ContentBlock;
  theme: Theme;
  pathForPageId: (pageId: string) => string | null;
  editable?: boolean;
  textEditable?: boolean;
  onChangeText?: (text: string) => void;
  onStartTextEdit?: () => void;
  onFinishTextEdit?: () => void;
  onOpenMedia?: () => void;
  mediaUrl?: (assetId: string) => string;
  style?: React.CSSProperties;
}

export function CanvasElement({ block, theme, pathForPageId, editable = false, textEditable = false, onChangeText, onStartTextEdit, onFinishTextEdit, onOpenMedia, mediaUrl, style }: CanvasElementProps): React.ReactElement | null {
  const customClasses = block.customClasses ?? [];
  const classes = (base: string) => [base, ...customClasses].filter(Boolean).join(' ');
  switch (block.type) {
    case 'heading': {
      const Tag = (`h${block.level ?? 2}`) as 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';
      return withElementIcon(block, <Tag className={classes('')} style={style}>{block.text}</Tag>);
    }

    case 'paragraph':
    case 'rich_text':
      return withElementIcon(block, <p className={classes('zs-rich-text')} style={style}>{block.text}</p>);

    case 'button':
      return <CanvasButton block={block} theme={theme} pathForPageId={pathForPageId} editable={textEditable} controlsEnabled={editable} onChangeText={onChangeText} onStartTextEdit={onStartTextEdit} onFinishTextEdit={onFinishTextEdit} style={style} customClassName={customClasses.join(' ')} />;

    case 'divider':
      return <hr className={classes(`zs-divider zs-divider-${block.variant ?? 'solid'}`)} style={block.typography?.color || block.typography?.customColor ? { borderTopColor: block.typography.customColor ?? `var(--zs-color-${block.typography.color})` } : undefined} />;

    case 'spacer':
      return <div aria-hidden="true" className={classes(`zs-spacer zs-spacer-${block.size ?? 'md'}`)} />;

    case 'badge':
      return withElementIcon(block, <span className={classes(`zs-badge zs-badge-${block.variant ?? 'pill'}`)} style={style}>{block.text}</span>);

    case 'feature_list':
      return (
        <ul className={classes('zs-feature-list')} style={style}>
          {(block.features ?? []).map((item, index) => (
            <li key={index}>
              <span aria-hidden="true">
                {block.icon === 'dot' ? <span className="zs-feature-list-dot">•</span> : <CanvasIcon name={block.icon === 'arrow' ? 'arrow-right' : 'check'} />}
              </span>
              {item.text}
            </li>
          ))}
        </ul>
      );

    case 'quote':
      return withElementIcon(block, (
        <figure className={classes('zs-quote')} style={style}>
          <blockquote>{block.text}</blockquote>
          {block.label || block.role ? (
            <figcaption>
              {block.label ?? ''}
              {block.role ? `${block.label ? ' · ' : ''}${block.role}` : ''}
            </figcaption>
          ) : null}
        </figure>
      ));

    case 'faq':
      return withElementIcon(block, (
        <div className={classes('zs-faq')} style={style}>
          {(block.questions ?? []).map((item, index) => (
            <details key={index}>
              <summary>{item.question}</summary>
              <p>{item.answer}</p>
            </details>
          ))}
        </div>
      ));

    case 'image': {
      const effects = block.effects ?? { radius: 'subtle' as const };
      return withElementIcon(block, (
        <figure className={classes(`zs-image ${imageAspectClass(block.aspect)} zs-surface`)} style={{ ...resolveSurface(theme, effects) as React.CSSProperties, ...style }}>
          {block.assetId && mediaUrl ? (
            <div className="zs-image-media-frame">
              <img src={mediaUrl(block.assetId)} alt={block.alt ?? ''} className="zs-image-media" />
              {editable && onOpenMedia ? (
                <button
                  type="button"
                  className="zs-image-edit-control"
                  aria-label="Afbeelding wijzigen"
                  onClick={(event) => { event.stopPropagation(); onOpenMedia(); }}
                >
                  Afbeelding wijzigen
                </button>
              ) : null}
            </div>
          ) : (
            <div className="zs-image-placeholder" role="img" aria-label={block.alt ?? 'Afbeelding'}>
              {block.assetId ? 'Afbeelding' : 'Afbeelding toevoegen'}
            </div>
          )}
          {block.caption ? <figcaption>{block.caption}</figcaption> : null}
        </figure>
      ));
    }

    case 'shape':
      return withElementIcon(block, (
        <div className="zs-shape-frame" aria-label="Visuele vorm">
          <div className={classes(`zs-shape zs-shape-${block.shape?.shape ?? 'rounded'}`)} aria-hidden="true" style={{ ...resolveVisualSurface(theme, block.shape ?? {}), ...style }} />
          {block.text || editable || textEditable ? (
            <div className="zs-shape-content">
              {(editable || textEditable) && onChangeText ? (
                <InlineText
                  value={block.text}
                  onChange={onChangeText}
                  placeholder="Tekst in de vorm"
                  multiline
                  as="p"
                  className="zs-shape-content-text"
                  editable={textEditable}
                  ariaLabel="Tekst in de vorm bewerken"
                  onFocus={onStartTextEdit}
                  onDoubleClick={onStartTextEdit}
                  onBlur={onFinishTextEdit}
                  style={style}
                />
              ) : <p className="zs-shape-content-text" style={style}>{block.text}</p>}
            </div>
          ) : null}
        </div>
      ));

    case 'gallery':
      return withElementIcon(block, (
        <div className={classes(`zs-gallery zs-gallery-${block.variant ?? 'grid'}`)} style={style}>
          {(block.assetIds ?? ['', '', '']).map((assetId, index) => (
            editable && onOpenMedia ? (
              <button type="button" className="zs-gallery-item cursor-pointer" key={`${assetId}-${index}`} aria-label={`Afbeelding ${index + 1} kiezen`} onClick={(event) => { event.stopPropagation(); onOpenMedia(); }}>
                {assetId ? `Afbeelding ${index + 1} wijzigen` : `Afbeelding ${index + 1} uploaden`}
              </button>
            ) : (
              <div className="zs-gallery-item" key={`${assetId}-${index}`} role="img" aria-label={`Afbeelding ${index + 1}`}>
                Afbeelding {index + 1}
              </div>
            )
          ))}
        </div>
      ));

    case 'cards': {
      const items = block.items ?? [];
      const effects = block.effects ?? { shadow: 'subtle' as const, radius: 'rounded' as const, hover: 'lift' as const };
      const hover = effectiveHover(effects.hover ?? 'none', false);
      const surface = resolveSurface(theme, effects);
      return (
        <div
          className={classes('zs-cards')}
          style={{ '--zs-card-columns': `repeat(${Math.min(Math.max(items.length, 1), 4)}, minmax(0, 1fr))`, ...style } as React.CSSProperties}
        >
          {items.map((item, index) => (
            <article
              className={`zs-card zs-surface zs-card-icon-position-${item.iconPosition ?? 'top'} ${hover !== 'none' ? `zs-surface-hover-${hover}` : ''}`}
              key={index}
              style={surface as React.CSSProperties}
            >
              <div className="zs-card-content">
                {item.icon && item.icon !== 'none' ? <span className="zs-card-icon"><CanvasIcon name={item.icon} /></span> : null}
                <div className="zs-card-copy"><h3>{item.title}</h3><p style={item.textFontSize ? { fontSize: `${item.textFontSize}px` } : undefined}>{item.text}</p></div>
              </div>
              {item.href ? <span className="zs-card-link" aria-hidden="true">Lees meer</span> : null}
            </article>
          ))}
        </div>
      );
    }

    case 'contact':
      return withElementIcon(block, (
        <address className={classes('zs-contact')} style={style}>
          <strong>{block.organizationName}</strong>
          {block.address ? <span>{block.address}</span> : null}
          {block.email ? <span>{block.email}</span> : null}
          {block.phone ? <span>{block.phone}</span> : null}
        </address>
      ));

    case 'form':
      return withElementIcon(block, (
        <form className={classes('zs-form')} style={style} onSubmit={(event) => event.preventDefault()}>
          {block.title ? <h3>{block.title}</h3> : null}
          {block.description ? <p className="zs-form-description">{block.description}</p> : null}
          {(block.fields ?? []).map((field) => (
            <label className="zs-form-field" key={field.fieldId}>
              <span>{field.label}{field.required ? ' *' : ''}</span>
              {field.type === 'textarea' ? <textarea placeholder={field.placeholder} disabled /> : field.type === 'select' ? <select disabled defaultValue=""><option value="">Kies een optie</option>{(field.options ?? []).map((option) => <option key={option}>{option}</option>)}</select> : field.type === 'checkbox' ? <input type="checkbox" disabled /> : <input type={field.type} placeholder={field.placeholder} disabled />}
            </label>
          ))}
          <button type="button" className="zs-button zs-button-primary" disabled>{block.submitLabel ?? 'Versturen'}</button>
        </form>
      ));

    case 'video':
      return withElementIcon(block, (
        <span className={classes('zs-video')} style={style}>
          {!block.icon || block.icon === 'none' ? <CanvasIcon name="play" /> : null}
          {block.caption ?? 'Video bekijken'}
        </span>
      ));

    case 'custom_html':
      return withElementIcon(block, (
        <div
          className={classes('rounded-xl border border-dashed border-violet-300 bg-violet-50/70 p-4 text-violet-950')}
          style={style}
          data-zoea-custom-html-preview="true"
        >
          <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.12em] text-violet-700">
            <span className="rounded bg-violet-200 px-1.5 py-0.5">HTML</span>
            Custom HTML
          </div>
          <p className="mt-2 text-sm leading-relaxed text-violet-900">
            {plainMarkupPreview(block.html ?? '') || 'Veilige eigen markup uit Developer Mode.'}
          </p>
          <p className="mt-2 text-xs text-violet-700">Bewerk de inhoud in Developer Mode.</p>
        </div>
      ));

    default:
      return null;
  }
}

function withElementIcon(block: ContentBlock, content: React.ReactElement): React.ReactElement {
  if (!block.icon || block.icon === 'none') return content;
  const position = block.iconPosition ?? 'top';
  const icon = <span className="zs-element-icon" aria-hidden="true"><CanvasIcon name={block.icon} /></span>;
  return <div className={`zs-element-with-icon zs-element-with-icon-${position}`}>{position === 'right' ? <>{content}{icon}</> : <>{icon}{content}</>}</div>;
}

function plainMarkupPreview(markup: string): string {
  return markup
    .replace(/<[^>]*>/g, ' ')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, 180);
}

function imageAspectClass(aspect: ContentBlock['aspect']): string {
  return `zs-image-${aspect === 'wide' || aspect === 'square' || aspect === 'portrait' || aspect === 'landscape' ? aspect : 'landscape'}`;
}

function CanvasButton({
  block,
  theme,
  pathForPageId,
  editable,
  controlsEnabled,
  onChangeText,
  onStartTextEdit,
  onFinishTextEdit,
  style,
  customClassName,
}: {
  block: ContentBlock;
  theme: Theme;
  pathForPageId: (pageId: string) => string | null;
  editable: boolean;
  controlsEnabled: boolean;
  onChangeText?: (text: string) => void;
  onStartTextEdit?: () => void;
  onFinishTextEdit?: () => void;
  style?: React.CSSProperties;
  customClassName?: string;
}) {
  const resolved = resolveButton(theme, legacyButtonPresentation(block));
  const destination = block.destination ?? (block.href ? { kind: 'url' as const, url: block.href } : null);
  const target = resolveDestination(destination, { pathForPageId });
  // Hover is previewed on the canvas so the owner can see what they picked;
  // the reduced-motion guard still applies through the mirrored stylesheet.
  const hover = effectiveHover(resolved.hoverName, false);

  const className = [
    'zs-button',
    `zs-button-${resolved.styleName}`,
    resolved.fullWidth ? 'zs-button-full' : '',
    hover !== 'none' ? `zs-button-hover-${hover}` : '',
    target ? '' : 'zs-button-inert',
    customClassName ?? '',
  ]
    .filter(Boolean)
    .join(' ');

  const icon = resolved.iconName ? <CanvasIcon name={resolved.iconName} /> : null;

  // Rendered as a <span>, never an <a>. A real anchor inside the Builder is a
  // trap: a stray click navigates away and the user loses their place, and
  // suppressing that with preventDefault still leaves middle-click and
  // context-menu "open in new tab" live. The element carries the same classes
  // and tokens, so it looks identical without being a link at all.
  return (
    <span
      className={className}
      style={{ ...resolved.variables as React.CSSProperties, ...style }}
      data-inert={target ? undefined : 'true'}
      title={target ? undefined : 'Deze knop heeft nog geen geldige bestemming.'}
    >
      {icon && resolved.iconPosition === 'left' ? icon : null}
      {controlsEnabled && onChangeText ? <InlineText value={block.label || block.text || 'Knop'} onChange={onChangeText} placeholder="Knoptekst" multiline={false} as="span" className="font-inherit" editable={editable} ariaLabel="Knoptekst bewerken" onFocus={onStartTextEdit} onDoubleClick={onStartTextEdit} onBlur={onFinishTextEdit} /> : <span onDoubleClick={onStartTextEdit}>{block.label || block.text || 'Knop'}</span>}
      {icon && resolved.iconPosition === 'right' ? icon : null}
    </span>
  );
}
